import {accessPolicy, refreshAccess} from "@/utils/managedAccess";
import Vue from "vue";
import Router from "vue-router";
import {canAccessRoute, getUser, getUserToken} from "../utils/helper";
import store from "../store";
// import { UserService } from '../common/api.service';
import {CHECK_AUTH} from "../store/actions.type";
import traineeFacilities from "../components/Lookups/trainee-facility/traineeFacility.vue";

Vue.use(Router);

const PRE_SERVICE_PROGRAM_ROUTE_MAP = {
    "tvet-program": "/pre-service/tvet-program",
    "generic-entry": "/pre-service/generic-entry",
    "post-basic": "/pre-service/post-basic",
    "post-graduate": "/pre-service/post-graduate",
    "graduate-entry": "/pre-service/graduate-entry",
};

const normalizePreServiceProgramKey = (value) => {
    return (value || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");
};

// const ifNotAuthenticated = async (to, from, next) => {
//   if (!getUserToken()) {
//     next();
//     return;
//   }
//   next("/");
// };

const ifAuthenticated = async (to, from, next) => {
    if (getUserToken()) {
        next();
        return;
    }
    next("/login");
};

const isAuthorized = async (to, from, next) => {
    if (to.type === "public") {
        return next();
    }

    const user = getUser();
    const roleName = user?.data?.userRoles?.roleName;
    if (to.name === "Config" && !accessPolicy(user)) {
        if (roleName === "System admins") {
            return next();
        }
        return next("/unauthorized");
    }
    if (!canAccessRoute(user, to)) {
        return next("/unauthorized");
    }

    return next();
};

export const router = new Router({
    mode: "history",
    meta: {title: "Home"},
    base: process.env.BASE_URL,
    routes: [
        {
            name: "Role permission preview",
            path: "/role-permissions-preview",
            type: "public",
            component: () => import("@/components/Settings/Role/RolePermissionsPreview.vue"),
        },
        {
            name: "iHRIS-HRD Landing",
            path: "/",
            alias: ["/landing", "/ihris-hrd"],
            type: "public",
            component: () => import("@/views/LandingPage"),
        },
        {
            path: "/app",
            name: "app",
            type: "public",
            beforeEnter: ifAuthenticated,
            component: () => import("@/views/Index"),
            children: [
                {
                    name: 'E-learning integrations', path: '/e-learning/integrations', type: 'private', beforeEnter: isAuthorized,
                    component: () => import('@/components/ELearning/ELearningIntegrations.vue'),
                },
                {
                    name: 'E-learning', path: '/e-learning', type: 'private', beforeEnter: isAuthorized,
                    component: () => import('@/components/ELearning/ELearningDashboard.vue'),
                },
                {
                    name: "Default",
                    path: "/hrd",
                    type: "public",
                    component: () => import("@/components/Dashboard/Dashboard"),
                },
                {
                    name: "Dashboard",
                    path: "/dashboard",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Dashboard/Dashboard"),
                },
                {
                    name: "Pre-Service",
                    path: "/preservice",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/PreService/PreService"),
                },
                {
                    name: "Map",
                    path: "/map",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Map/Map"),
                },
                {
                    name: "Online Status",
                    path: "/onlineStatus",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/OnlineStatus/onlineStatus.vue"),
                },
                {
                    name: "Course",
                    path: "/course",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Courses/Course"),
                },
                {
                    name: "CPD Activity",
                    path: "/cpdActivity",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Programs/Program"),
                },
                {
                    name: "CPD Provider",
                    path: "/cpdProvider",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/TrainingCenter/TrainingCenter"),
                },
                {
                    name: "Trainer",
                    path: "/trainer",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Trainers/Trainer"),
                },
                {
                    name: "Monitoring",
                    path: "/graduateTracking",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/GraduateTracks/GraduateTracking"),
                },
                {
                    name: "Trainee",
                    path: "/traineeRegistration",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Trainees/Trainee"),
                },
                {
                    name: "Pre-service",
                    path: "/pre-service",
                    type: "private",
                    beforeEnter: isAuthorized,
                    redirect: (to) => {
                        const selectedProgramKey = normalizePreServiceProgramKey(to.query?.tab);
                        const targetPath = PRE_SERVICE_PROGRAM_ROUTE_MAP[selectedProgramKey] || PRE_SERVICE_PROGRAM_ROUTE_MAP["tvet-program"];
                        const nextQuery = {...to.query};
                        delete nextQuery.tab;
                        return {
                            path: targetPath,
                            query: nextQuery,
                        };
                    },
                    accessAliases: ["Pre-service", "/pre-service"],
                },
                {
                    name: "TVET Program",
                    path: "/pre-service/tvet-program",
                    type: "private",
                    beforeEnter: isAuthorized,
                    accessAliases: ["Pre-service", "/pre-service"],
                    component: () => import("@/components/Graduates/Graduates"),
                    props: {programKey: "tvet-program"},
                },
                {
                    name: "Generic Entry",
                    path: "/pre-service/generic-entry",
                    type: "private",
                    beforeEnter: isAuthorized,
                    accessAliases: ["Pre-service", "/pre-service"],
                    component: () => import("@/components/Graduates/Graduates"),
                    props: {programKey: "generic-entry"},
                },
                {
                    name: "Post Basic",
                    path: "/pre-service/post-basic",
                    type: "private",
                    beforeEnter: isAuthorized,
                    accessAliases: ["Pre-service", "/pre-service"],
                    component: () => import("@/components/Graduates/Graduates"),
                    props: {programKey: "post-basic"},
                },
                {
                    name: "Post Graduate",
                    path: "/pre-service/post-graduate",
                    type: "private",
                    beforeEnter: isAuthorized,
                    accessAliases: ["Pre-service", "/pre-service"],
                    component: () => import("@/components/Graduates/Graduates"),
                    props: {programKey: "post-graduate"},
                },
                {
                    name: "Graduate Entry",
                    path: "/pre-service/graduate-entry",
                    type: "private",
                    beforeEnter: isAuthorized,
                    accessAliases: ["Pre-service", "/pre-service"],
                    component: () => import("@/components/Graduates/Graduates"),
                    props: {programKey: "graduate-entry"},
                },
                {
                    name: "Program Event",
                    path: "/programEvent",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/ProgramEvents/ProgramEvent"),
                },
                {
                    name: "Users",
                    path: "/user",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Settings/User/User"),
                },
                {
                    name: "User Groups",
                    path: "/userGroup",
                    redirect: {path: "/role"},
                },
                {
                    name: "Roles",
                    path: "/role",
                    meta: {accessAliases: ["User Groups", "Permissions"]},
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Settings/Role/Role"),
                },
                {
                    name: "Menu Items",
                    path: "/menuItem",
                    type: "private",
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Settings/MenuItem/MenuItem"),
                },
                {
                    name: "Permissions",
                    path: "/permission",
                    redirect: {path: "/role"},
                },
                {
                    name: "Register Trainee",
                    path: "/registerTrainee",
                    type: "public",
                    props: true,
                    // beforeEnter:isAuthorized,
                    component: () =>
                        import("@/components/Trainees/new-trainee/hrd-register-trainee"),
                },
                {
                    name: "Training Event Report",
                    path: "/trainingEventReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/registration-report/registration-report"
                            ),
                },
                {
                    name: "CPD Provider Report",
                    path: "/cpdReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/cpd-provider-report/cpd-provider-report"
                            ),
                },
                {
                    name: "CPD Accreditor",
                    path: "/Accreditor",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Accreditors/hrd-accreditors"),
                },
                {
                    name: "CPD Accreditor Report",
                    path: "/accreditorReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/accreditor-report/accreditor-report"),
                },
                {
                    name: "Accredited Course Report",
                    path: "/accreditedCourseReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/accredited-course-report/accredited-course-report"
                            ),
                },
                {
                    name: "Accreditation Summary Report",
                    path: "/accreditationSummaryReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/accreditation-summary-report/accreditation-summary-report"
                            ),
                },
                {
                    name: "Accredited CPD Provider Report",
                    path: "/accreditedCpdProviderReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/accredited-cpd-provider-report/accredited-cpd-provider-report"
                            ),
                },
                {
                    name: "Training Detail Report",
                    path: "/trainingDetailReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Reports/training-summary-report/training-summary-report"
                            ),
                },
                {
                    name: "Preservice Report",
                    path: "/preserviceReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/preservice-report/pre-service-report"),
                },
                {
                    name: "Renewal Status Report",
                    path: "/renewalStatusReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/renewal-status-report/renewal-status-report"),
                },
                {
                    name: "Trainee Report",
                    path: "/traineeReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/trainee-report/trainee-report"),
                },
                {
                    name: "Trainer Report",
                    path: "/trainerReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/trainer-report/trainer-report.vue"),
                },
                {
                    name: "Course Report",
                    path: "/courseReport",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Reports/course-report/course-report.vue"),
                },
                {
                    name: "Health Professions",
                    path: "/healthProfessions",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/health-professions/HealthProfessions"),
                },
                {
                    name: "Mfr",
                    path: "/mfr",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () => import("@/components/Lookups/mfr/mfr"),
                },
                {
                    name: "Universities",
                    path: "/universities",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/universities/universities"),
                },
                {
                    name: "Certificate Reasons",
                    path: "/certificateReasons",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Lookups/certificate-reasons/certificateReasons"
                            ),
                },
                {
                    name: "Program Education",
                    path: "/educationalPrograms",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import(
                            "@/components/Lookups/educational-program/educationalProgram"
                            ),
                },
                {
                    name: "Disciplines",
                    path: "/disciplines",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/disciplines/disciplines"),
                },
                {
                    name: "CPD Activity Category",
                    path: "/cpdActivityCategory",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/cpd-activity-category/cpdActivityCategory"),
                },
                {
                    name: "Cat II Requirement",
                    path: "/catIIRequirement",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/cat-ii-requirement/cat-ii-requirement"),
                },
                {
                    name: "Level of Studies",
                    path: "/levelOfStudies",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/level-of-studies/levelOfStudies"),
                },
                {
                    name: "Ethnicity",
                    path: "/ethnicity",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/ethnicity/ethnicity"),
                },
                {
                    name: "Organization",
                    path: "/organizations",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/organizations/organizations"),
                },
                {
                    name: "Config",
                    path: "/generalTimes",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/general-time/generalTimes"),
                },
                {
                    name: "Facilities",
                    path: "/Facilities",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () =>
                        import("@/components/Lookups/user-facility/userFacility"),
                },
                {
                    name: "Trainee Facilities",
                    path: "/traineeFacilities",
                    type: "private",
                    props: true,
                    beforeEnter: isAuthorized,
                    component: traineeFacilities,
                },
                {
                    name: 'Trainer Selection Criteria',
                    path: '/trainerSelectionCriteria',
                    type: 'private',
                    props: true,
                    beforeEnter: isAuthorized,
                    component: () => import('@/components/Lookups/trainer-selection-criteria/trainerCriteria')
                },
            ],
        },
        {
            name: "Login",
            path: "/login",
            // beforeEnter: ifNotAuthenticated,
            type: "public",
            component: () => import("@/components/Login/Login"),
        },
        {
            name: "Reset Password",
            path: "/resetPassword/:token",
            type: "private",
            component: () => import("@/components/Login/ResetPassword"),
        },
        {
            name: "Unauthorized",
            path: "/unauthorized",
            type: "private",
            component: () => import("@/views/components/Unauthorized"),
        },
            {
                name: "Trainer Information",
                path: "/trainerInformation/:id/:category/:courseId",
                type: "public",
                // beforeEnter:isAuthorized,
                component: () => import("@/components/Trainers/trainer-public-detail"),
            },
            {
                name: "Trainee Information",
                path: "/traineeInformation/:id",
                type: "public",
                // beforeEnter:isAuthorized,
                component: () => import("@/components/Trainees/trainee-public-detail"),
            },
            {
                name: "New Trainee Information",
                path: "/newTraineeInformation/:id",
                type: "public",
                // beforeEnter:isAuthorized,
                component: () => import("@/components/Trainees/new-trainee-public-info"),
            },
    ],
});

router.beforeEach(async (to, from, next) => {
    if (getUserToken() && !["/", "/login", "/unauthorized", "/role-permissions-preview"].includes(to.path)) {
        await store.dispatch(CHECK_AUTH);
        try { await refreshAccess(); } catch (error) { return next("/unauthorized"); }
    }
    if (!store.getters.currentUser.token) {
        await Promise.all([store.dispatch(CHECK_AUTH)]);
    }
    next();
});

export default router;
